import { useMemo } from 'react';

import { USER_TYPE } from '@/common/constants';
import { useUserInfo } from '@/common/hooks';
import { useFastshiftSelector } from '@/state';

import { CustomerIdentityAttrs, BaseIdentityAttrs } from '../types';

import { useBaseIdentityAttrs } from './use-base-identity-attrs';

/**
 * @param allowFallback - Set to true for UI events, false for critical events (page/track)
 */
export const useCustomerIdentityAttrs = (
	allowFallback = false,
): CustomerIdentityAttrs | BaseIdentityAttrs | null => {
	const baseAttrs = useBaseIdentityAttrs();
	const { data: customerDomain } = useFastshiftSelector((state) => state.customerDomain);
	const { data: user } = useUserInfo();

	return useMemo(() => {
		if (!baseAttrs) return null;

		if (user?.userType === USER_TYPE.CUSTOMER) {
			return {
				...baseAttrs,
				selectedCustomerDomain: user.userDomain.domain,
				selectedCustomerDomainId: user.userDomain.domainId,
			};
		}

		if (customerDomain) {
			return {
				...baseAttrs,
				selectedCustomerDomain: customerDomain.customerDomain,
				selectedCustomerDomainId: customerDomain.customerDomainId,
			};
		}

		// When customerDomain not ready: fallback to baseAttrs for UI events, null for critical events
		return allowFallback ? baseAttrs : null;
	}, [baseAttrs, customerDomain, allowFallback, user]);
};
